<template>
  <div>
    <McDraggablePopup
      :id="vehicleStatus.vehicleId.toString()"
      :title="vehicleStatus.licensePlate"
      variant="primary"
      :initialX="position.x"
      :initialY="position.y"
      scrollable
      @close="closePopup"
    >
      <div class="mc-control-tower__detail">
        <div class="mc-control-tower__detail-map">
          <div v-if="detailed">
            <span class="mc-control-tower__detail-map-back" @click="expandMap">
              <i class="mc-fa icon-outline_arrow-left"></i>
              {{ $t('controlTower.detail.otherDetail') }}
            </span>
            <div class="mt-3 mc-control-tower__detail-map-wrapper">
              <div class="mc-map-alert-icon-map">
                <McButton
                  variant="secondary"
                  icon="icon-fill_target"
                  text=""
                  size="sm"
                  @click="recenterMap"
                />
              </div>
              <GmapMap
                id="map"
                ref="detailAlertMap"
                map-type-id="roadmap"
                :center="{
                  lat: parseFloat(vehicleStatus.latitude),
                  lng: parseFloat(vehicleStatus.longitude),
                }"
                :options="mapOptions"
                :zoom="18"
                class="mc-control-tower__detail-map-gmap--detail"
                @click="createCustomInfoWindow"
              >
                <gmap-custom-marker
                  :marker="{
                    lat: parseFloat(vehicleStatus.latitude),
                    lng: parseFloat(vehicleStatus.longitude),
                  }"
                  :offsetY="25"
                  :clickable="false"
                  :draggable="false"
                >
                  <span class="mc-car-ico active">
                    <img
                      :src="vehicleIconSrc"
                      :style="`transform: rotate(${parseInt(vehicleStatus.direction)}deg);`"
                      alt="car-icon"
                    />
                  </span>
                </gmap-custom-marker>
              </GmapMap>
            </div>
          </div>

          <div v-else>
            <div class="d-flex mb-2 justify-content-between">
              <span>{{ vehicleStatus.vehicle.driver1?.fullname ?? '-' }}</span>
              <span>
                {{ vehicleStatus.vehicle.device?.phoneNo ?? '-' }}
                <template v-if="isCurrentVehicleVoipActive">
                  <McButton
                    variant="secondary-red"
                    icon="icon-outline_phone-off"
                    text=""
                    class="ms-2"
                    size="sm"
                    :title="$t('trackVideo.endCall')"
                    @click="endCall"
                  />
                </template>
                <template v-else>
                  <!-- For BSJ VoIP -->
                  <McButton
                    v-if="
                      hasAccessAll([
                        'b3f-two-way-communication:read',
                        'b3f-two-way-communication-all-module:read',
                      ]) && callState.isHaveVoip
                    "
                    variant="secondary"
                    icon="icon-outline_phone"
                    :disabled="
                      (vehicleIdsCallActive.length >= $store.state.voip.maxCall &&
                        !isCurrentVehicleVoipActive) ||
                      $store.state.voip.isBroadcastActive
                    "
                    text=""
                    class="ms-2"
                    size="sm"
                    :title="$t('notification.action.callDriver')"
                    @click="callDriver(true)"
                  />
                  <!-- For Concox Register Call Number -->
                  <McButton
                    v-else-if="
                      hasAccessAll(['b1d-liveview-setregistercall:read']) &&
                      isHaveRegisterCallNumber(item.vehicle.device.deviceCode)
                    "
                    variant="secondary"
                    icon="icon-outline_phone"
                    text=""
                    class="ms-2"
                    size="sm"
                    :title="$t('notification.action.callDriver')"
                    @click="callDriver()"
                  />
                </template>
              </span>
            </div>
            <div class="form-check form-switch form-switch-sm mb-2">
              <input v-model="fleetInfo" class="form-check-input" type="checkbox" />
              {{ $t('master.form.fleet.fleetInformation') }}
            </div>
            <div v-if="fleetInfo" class="d-flex flex-column font-size-12 ms-2 mb-1">
              <label>
                <span class="text-muted">{{ $t('global.field.vehicleType') }}:</span>
                {{
                  vehicleStatus.vehicle.vehicleType
                    ? `${vehicleStatus.vehicle.vehicleType.manufacturer}/${vehicleStatus.vehicle.vehicleType.model}/${vehicleStatus.vehicle.vehicleType.type}`
                    : '-'
                }}
              </label>
              <label>
                <span class="text-muted">{{ $t('global.object.hullNo') }}:</span>
                {{ vehicleStatus.vehicle.hullNo ?? '-' }}
              </label>
            </div>
            <div class="mc-control-tower__detail-map-wrapper">
              <div class="mc-map-alert-icon-small-switch">
                <span v-if="gpsSourceInfo?.isGpsSource" class="signal-on">
                  <i
                    v-if="gpsSourceInfo?.signalStrength > 2"
                    v-b-tooltip.html
                    :title="$t('controlTower.modal.locationBasedOnGPS')"
                    class="mc-icon icon-fill_signal-high"
                  ></i>
                  <img
                    v-else-if="gpsSourceInfo?.signalStrength == 2"
                    v-b-tooltip.html
                    :title="$t('controlTower.modal.locationBasedOnGPS')"
                    src="/img/liveview/med.png"
                    alt="med-signal"
                  />
                  <img
                    v-else-if="gpsSourceInfo?.signalStrength == 1"
                    v-b-tooltip.html
                    :title="$t('controlTower.modal.locationBasedOnGPS')"
                    src="/img/liveview/low.png"
                    alt="low-signal"
                  />
                  <i
                    v-else
                    v-b-tooltip.html
                    :title="$t('controlTower.modal.locationBasedOnGPS')"
                    class="mc-icon icon-fill_signal-high"
                  ></i>
                </span>
                <span v-else-if="gpsSourceInfo?.isCamSource" class="signal-on">
                  <i
                    v-if="gpsSourceInfo?.signalStrength > 2"
                    v-b-tooltip.html
                    :title="$t('controlTower.modal.locationBasedOnCamera')"
                    class="mc-icon icon-fill_signal-high"
                  ></i>
                  <img
                    v-else-if="gpsSourceInfo?.signalStrength == 2"
                    v-b-tooltip.html
                    :title="$t('controlTower.modal.locationBasedOnCamera')"
                    src="/img/liveview/med.png"
                    alt="med-signal"
                  />
                  <img
                    v-else-if="gpsSourceInfo?.signalStrength == 1"
                    v-b-tooltip.html
                    :title="$t('controlTower.modal.locationBasedOnCamera')"
                    src="/img/liveview/low.png"
                    alt="low-signal"
                  />
                  <i
                    v-else
                    v-b-tooltip.html
                    :title="$t('controlTower.modal.locationBasedOnCamera')"
                    class="mc-icon icon-fill_signal-high"
                  ></i>
                </span>
                <span v-else class="signal-off">
                  <i
                    v-b-tooltip
                    :title="$t('controlTower.modal.locationNotUpdated')"
                    class="mc-icon icon-fill_pin-off"
                    placement="bottomright"
                  ></i>
                </span>
              </div>
              <div class="mc-map-alert-icon-small-expand">
                <McButton
                  variant="secondary"
                  icon="icon-outline_expand"
                  text=""
                  size="sm"
                  @click="expandMap"
                />
              </div>
              <div class="mc-map-alert-icon-small-map">
                <McButton
                  variant="secondary"
                  icon="icon-fill_target"
                  text=""
                  size="sm"
                  @click="recenterMap"
                />
              </div>
              <GmapMap
                id="map"
                ref="detailAlertMap"
                map-type-id="roadmap"
                :center="{
                  lat: parseFloat(vehicleStatus.latitude),
                  lng: parseFloat(vehicleStatus.longitude),
                }"
                :options="mapOptions"
                :zoom="18"
                class="mc-control-tower__detail-map-gmap--small"
              >
                <gmap-custom-marker
                  :marker="{
                    lat: parseFloat(vehicleStatus.latitude),
                    lng: parseFloat(vehicleStatus.longitude),
                  }"
                  :offsetY="25"
                  :clickable="false"
                  :draggable="false"
                >
                  <span class="mc-car-ico active">
                    <img
                      :src="vehicleIconSrc"
                      :style="`transform: rotate(${parseInt(vehicleStatus.direction)}deg);`"
                      alt="car-icon"
                    />
                  </span>
                </gmap-custom-marker>
              </GmapMap>
            </div>
          </div>
          <p class="mc-control-tower__detail-map-address">
            {{ vehicleStatus.address ?? '-' }}
          </p>
        </div>

        <div class="mc-control-tower__detail-action-page">
          <div class="d-flex gap-1" @click="redirectToLiveview">
            <i class="mc-fa icon-outline_live-view font-size-16"></i>
            {{ $t('tower.trackVehicles') }}
          </div>
          <template v-if="detailed && isCurrentVehicleVoipActive">
            <div class="d-flex gap-1 text-danger" @click="endCall()">
              <i class="mc-fa icon-outline_phone-off font-size-16"></i>
              {{ $t('trackVideo.endCall') }}
            </div>
          </template>
          <template v-else>
            <!-- For BSJ VoIP -->
            <div
              v-if="
                detailed &&
                hasAccessAll([
                  'b3f-two-way-communication:read',
                  'b3f-two-way-communication-all-module:read',
                ]) &&
                callState.isHaveVoip
              "
              class="d-flex gap-1"
              :class="{
                'opacity-50':
                  (vehicleIdsCallActive.length >= $store.state.voip.maxCall &&
                    !isCurrentVehicleVoipActive) ||
                  $store.state.voip.isBroadcastActive,
              }"
              @click="
                ;(vehicleIdsCallActive.length >= $store.state.voip.maxCall &&
                  !isCurrentVehicleVoipActive) ||
                $store.state.voip.isBroadcastActive
                  ? undefined
                  : callDriver(true)
              "
            >
              <i class="mc-fa icon-outline_phone font-size-16"></i>
              {{ $t('global.action.callDriver') }}
            </div>
            <!-- For Concox Register Call Number -->
            <div
              v-else-if="
                detailed &&
                hasAccessAll(['b1d-liveview-setregistercall:read']) &&
                isHaveRegisterCallNumber(item.vehicle.device.deviceCode)
              "
              class="d-flex gap-1"
              @click="callDriver()"
            >
              <i class="mc-fa icon-outline_phone font-size-16"></i>
              {{ $t('global.action.callDriver') }}
            </div>
          </template>
          <div
            v-if="
              hasAccessAll(['b3d-pelacakan-video-new:read', 'vehicle_video_trackers:read']) &&
              !detailed
            "
            class="d-flex gap-1"
            @click="newWindowTrackVision"
          >
            <i class="mc-fa icon-outline_video font-size-16"></i>
            {{ $t('menuitems.trackVideo.title') }}
          </div>
        </div>
        <div v-show="!detailed">
          <NotificationSection
            :ctData="vehicleStatus.controlTowerData"
            :licensePlate="vehicleStatus.licensePlate"
            :vehicleId="vehicleStatus.vehicleId"
            :urgentAlertRules="urgentAlertRules"
            :importantAlertRules="importantAlertRules"
          />
          <div class="mc-control-tower__detail-expcol">
            <div class="mc-control-tower__detail-expcol-item">
              <div class="mc-control-tower__detail-expcol-header">
                <h5>
                  <i
                    :class="
                      expanded.trip
                        ? 'mc-fa icon-outline_chevron-up'
                        : 'mc-fa icon-outline_chevron-down'
                    "
                    @click="handleCollapseExpand('trip')"
                  ></i>
                  {{ $t('global.object.trip') }}
                </h5>
                <McButton
                  variant="secondary"
                  icon="icon-outline_map"
                  :text="$t('global.object.tripHistory')"
                  size="sm"
                  @click="redirectToTripHistory"
                />
              </div>
              <div v-if="expanded.trip" class="mc-control-tower__detail-expcol-body">
                <div class="mc-trip-card">
                  <div class="card-header">
                    {{ $t('controlTower.detail.latestInfo') }}
                  </div>
                  <div class="card-body">
                    <div class="row">
                      <div class="col-md-6">
                        <dl class="mc-modal-info__list-detail">
                          <dt>{{ $t('global.object.speed') }}</dt>
                          <dd>
                            {{ `${vehicleStatus.calculatedSpeed} Km/${$t('global.time.hour')}` }}
                          </dd>
                        </dl>
                      </div>
                      <div class="col-md-6">
                        <dl class="mc-modal-info__list-detail">
                          <dt>{{ $t('global.object.machineStatus') }}</dt>
                          <dd>
                            {{
                              vehicleStatus.engineOn
                                ? $t('global.object.on')
                                : $t('global.object.off')
                            }}
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="mc-trip-card">
                  <div class="card-header">
                    {{ $t('controlTower.detail.todayRecap') }}
                  </div>
                  <div class="card-body">
                    <div class="row">
                      <div class="col-md-6">
                        <dl class="mc-modal-info__list-detail">
                          <dt>{{ $t('global.object.mileage') }}</dt>
                          <dd>{{ `${vehicleStatus.sumDistance} Km` }}</dd>
                        </dl>
                      </div>
                      <div class="col-md-6">
                        <dl class="mc-modal-info__list-detail">
                          <dt>{{ $t('liveView.modal.drivingDuration') }}</dt>
                          <dd>{{ vehicleStatus.sumDrivetimeFormatted }}</dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="mc-control-tower__detail-expcol-item">
              <div class="mc-control-tower__detail-expcol-header">
                <h5>
                  <i
                    :class="
                      expanded.tracker
                        ? 'mc-fa icon-outline_chevron-up'
                        : 'mc-fa icon-outline_chevron-down'
                    "
                    @click="handleCollapseExpand('tracker')"
                  ></i>
                  {{ `Sensor & ${$t('master.pelacak')}` }}
                </h5>
              </div>
              <div v-if="expanded.tracker" class="mc-control-tower__detail-expcol-body">
                <ul class="mc-modal-info__list">
                  <li>
                    <b-row class="mc-modal-info__list-detail-row">
                      <b-col md="6">
                        <dl class="mc-modal-info__list-detail">
                          <dt>{{ $t('global.object.fuel') }}</dt>
                          <dd>
                            {{
                              `${(
                                (vehicleStatus?.fuelCapacity * vehicleStatus?.fuelFiltered) /
                                100
                              ).toFixed(2)} ${$t('dashboard.object.liter')} (${
                                vehicleStatus?.fuelFiltered
                              }%)`
                            }}
                          </dd>
                        </dl>
                      </b-col>
                      <b-col md="6">
                        <dl class="mc-modal-info__list-detail">
                          <dt>{{ 'Voltase' }}</dt>
                          <dd>
                            {{
                              vehicleStatus.accuVoltage ? `${vehicleStatus.accuVoltage} Volt` : '-'
                            }}
                          </dd>
                        </dl>
                      </b-col>
                      <b-col md="6">
                        <dl class="mc-modal-info__list-detail">
                          <dt>{{ $t('global.alert.temperature') }}</dt>
                          <dd>
                            <template v-if="vehicleStatus.temperature">
                              <span v-for="(row, index) in vehicleStatus.temperature" :key="index">
                                {{ row ? `${row} &#186; C ` : '-' }}
                              </span>
                            </template>
                            <template v-else>
                              {{ '-' }}
                            </template>
                          </dd>
                        </dl>
                      </b-col>
                      <b-col md="6">
                        <dl class="mc-modal-info__list-detail">
                          <dt>{{ $t('global.alert.refrigerator') }}</dt>
                          <dd>
                            {{
                              vehicleStatus.refrigeratorStatus
                                ? vehicleStatus.refrigeratorStatus === 1
                                  ? $t('global.object.on')
                                  : $t('global.object.off')
                                : '-'
                            }}
                          </dd>
                        </dl>
                      </b-col>
                      <b-col md="6">
                        <dl class="mc-modal-info__list-detail">
                          <dt>{{ $t('liveView.infoWindow.imei') }}</dt>
                          <dd>{{ vehicleStatus.vehicle.imei ?? '-' }}</dd>
                        </dl>
                      </b-col>
                      <b-col md="6">
                        <dl class="mc-modal-info__list-detail">
                          <dt>{{ $t('global.object.phoneNumber') }}</dt>
                          <dd>{{ vehicleStatus.vehicle.device?.phoneNo ?? '-' }}</dd>
                        </dl>
                      </b-col>
                      <b-col md="12">
                        <McButton
                          v-if="
                            getAccessRight() &&
                            !hasCutOffSensor &&
                            !getFeatureFlagValue('hideOldCutOffVehicleFlag')
                          "
                          variant="secondary"
                          icon="icon-outline_engine"
                          :text="$t('controlTower.detail.cutOffEngine')"
                          size="sm"
                          :disabled="
                            !hasAccessAll([
                              'b1c-liveview-setengine:read',
                              'set_engine_status:update',
                            ])
                          "
                          @click="cutOffEngine"
                        />
                      </b-col>
                      <b-col md="12">
                        <McButton
                          v-if="
                            getAccessRight() &&
                            hasCutOffSensor &&
                            getFeatureFlagValue('cutOffVehicleFlag')
                          "
                          variant="secondary"
                          icon="icon-outline_engine"
                          :text="$t('controlTower.detail.cutOffVehicle')"
                          size="sm"
                          :disabled="
                            !hasAccessAll([
                              'b1c-liveview-setengine:read',
                              'set_engine_status:update',
                            ]) || !hasCutOffSensor
                          "
                          @click="cutOffVehicle"
                        />
                      </b-col>
                    </b-row>
                  </li>
                </ul>
              </div>
            </div>

            <div
              v-if="
                hasAccessAll([
                  'fleet-task-instant:read',
                  'fleet-task-instant:create',
                  'fleet-task-instant:update',
                  'fleet-task-instant:delete',
                ])
              "
              class="mc-control-tower__detail-expcol-item"
            >
              <div class="mc-control-tower__detail-expcol-header">
                <h5>
                  <i
                    :class="
                      expanded.assignment
                        ? 'mc-fa icon-outline_chevron-up'
                        : 'mc-fa icon-outline_chevron-down'
                    "
                    @click="handleCollapseExpand('assignment')"
                  ></i>
                  {{ $t('controlTower.detail.fleetTask') }}
                </h5>
              </div>
              <FOSection
                v-if="expanded.assignment"
                :data="localItem?.controlTowerData?.fleetTaskInstant"
              />
            </div>
          </div>
        </div>
      </div>
      <template #footer>
        <div class="d-flex float-end mb-4">
          <McButton
            variant="secondary"
            icon="icon-outline_share"
            :text="$t('liveView.placeholder.shareLocation')"
            size="sm"
            @click="shareLocation"
          />
        </div>
      </template>
    </McDraggablePopup>

    <ModalCall />
    <ModalShare :item="vehicleStatus" />
    <ModalEngine
      v-if="hasAccessAll(['b1c-liveview-setengine:read', 'set_engine_status:update'])"
      :imei="vehicleStatus.vehicle?.imei"
    />
    <ModalCutOffVehicle
      v-if="hasAccessAll(['b1c-liveview-setengine:read', 'set_engine_status:update'])"
      :imei="vehicleStatus.vehicle?.imei"
      :sensor="sensor"
    />
  </div>
</template>
<script>
import McDraggablePopup from '@/components/McDraggablePopup'
import McButton from '@/components/McButton'
import ModalCall from './ModalCall'
import ModalShare from './ModalShare'
import ModalEngine from './ModalEngine'
import ModalCutOffVehicle from './ModalCutOffVehicle'
import FOSection from '../component/FOSection.vue'
import NotificationSection from '../component/NotificationSection.vue'
import { Helper } from '@/mixin/helper'
import { MixPanel } from '@/mixin/mixpanel'
import { CTOWER_USER_PLACARDS, UPDATE_CTOWER_USER_PLACARD } from '@/services/api/controltower'
import moment from 'moment'
import { getAddress, getGeolocation } from '@/services/api/geolocation'
import { GetAddressData, GetAddress } from '@/services/api/map'
import GmapCustomMarker from 'vue2-gmap-custom-marker'
import { mapGetters } from 'vuex'
import { GET_ALERT_CENTER } from '@/services/api/notification/general'

moment.locale('id')

export default {
  components: {
    McDraggablePopup,
    McButton,
    ModalCall,
    ModalShare,
    ModalEngine,
    ModalCutOffVehicle,
    FOSection,
    NotificationSection,
    'gmap-custom-marker': GmapCustomMarker,
  },
  mixins: [Helper, MixPanel],
  props: {
    item: {
      type: Object,
      required: true,
    },
    position: {
      type: Object,
      default: () => ({ x: 200, y: 100 }),
    },
    period: {
      type: Array,
      default: () => [],
    },
    urgentAlertRules: {
      type: Array,
      default: () => [],
    },
    importantAlertRules: {
      type: Array,
      default: () => [],
    },
    callState: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isActive: false,
      layouts: [],
      oldData: [],
      loading: false,
      needUpdate: true,
      phaseOneShow: false,
      mapOptions: {
        zoomControl: false,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        clickableIcons: false, // This disables Google's POI clicks
        disableDefaultUI: true, // This removes all default UI controls
      },
      detailed: false,

      expanded: {
        trip: false,
        tracker: false,
        assignment: false,
      },

      fleetInfo: false,
      selectedPosition: { lat: null, lng: null },
      currentInfoWindow: null,
      sensor: [],
      localItem: null,
      vehicleStatus: null,
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
      getCutOffSensorCodes: 'sensor/getCutOffSensorCodes',
    }),
    ...mapGetters('voip', ['vehicleIdsCallActive']),
    vehicleIconSrc() {
      const status = this.vehicleStatus.calculatedMotionStatus
      const icon = this.vehicleStatus.vehicle?.vehicleType?.icon ?? 'van'
      const color =
        {
          P: 'red',
          A: 'blue',
          M: 'green',
          I: 'orange',
          O: 'grey',
          U: 'grey',
        }[status] || 'grey'

      return require(`@/assets/images/vehicles/${icon}/${icon}-${color}.png`)
    },

    hasCutOffSensor() {
      return this.sensor.some((sensorCode) =>
        this.getCutOffSensorCodes.includes(Number(sensorCode))
      )
    },

    gpsSourceInfo() {
      const MOTION_STATUS = {
        UNKNOWN: 'U',
        OFFLINE: 'O',
      }

      const DEVICE_SOURCE = {
        CAMERA: 'cam',
        GPS: 'gps',
      }

      const vehicle = this.vehicleStatus
      if (!vehicle) return { isCamSource: false, isGpsSource: false }

      const isValidStatus = (status) =>
        status && status !== MOTION_STATUS.UNKNOWN && status !== MOTION_STATUS.OFFLINE

      const deviceSource = vehicle.deviceSource
      const parentMotionStatus = vehicle.calculatedMotionStatus
      const childMotionStatus = vehicle.children?.[0]?.calculatedMotionStatus

      const hasCamSensor = vehicle.vehicle?.sensor?.some((s) => s.sensorType?.sensorCode == 35)

      const isCamSource =
        deviceSource === DEVICE_SOURCE.CAMERA &&
        isValidStatus(hasCamSensor ? parentMotionStatus : childMotionStatus)
      const isGpsSource = deviceSource === DEVICE_SOURCE.GPS && isValidStatus(parentMotionStatus)

      const signalStrength = isCamSource
        ? vehicle.children[0]?.signalStrength
        : vehicle.signalStrength

      return {
        isCamSource,
        isGpsSource,
        signalStrength,
      }
    },

    isCurrentVehicleVoipActive() {
      return this.vehicleIdsCallActive.includes(this.callState.vehicleIdVoip)
    },
  },

  watch: {
    item: {
      async handler(newVal) {
        if (!newVal) return
        //force set device source if null
        if (!newVal.deviceSource) {
          const hasCamSensor = newVal.vehicle?.sensor?.some((s) => s.sensorType?.sensorCode == 35)
          if (hasCamSensor) newVal.deviceSource = 'cam'
          else newVal.deviceSource = 'gps'
        }

        this.localItem = {
          ...newVal,
          deviceSource:
            newVal.deviceSource == 'cam' ? 'cam' : newVal.deviceSource == 'gps' ? 'gps' : undefined,
        }

        this.vehicleStatus = {
          ...newVal,
          deviceSource:
            newVal.deviceSource == 'cam' ? 'cam' : newVal.deviceSource == 'gps' ? 'gps' : undefined,
        }

        this.$forceUpdate()

        if (newVal?.vehicleId) {
          if (this.needUpdate) this.getPlacards()
        }
        if (!newVal.address) {
          this.fetchAddress(newVal)
        }
      },
      deep: true,
      immediate: true,
    },
    'item.updateAlert': {
      async handler(newVal) {
        if (newVal) {
          this.localItem = {
            ...this.localItem,
            controlTowerData: {
              ...this.localItem.controlTowerData,
              urgent: {
                ...this.localItem.controlTowerData.urgent,
                unresolvedAlerts: await this.getAlerts(true, false, false),
              },
              important: {
                ...this.localItem.controlTowerData.important,
                unresolvedAlerts: await this.getAlerts(false, true, false),
              },
            },
          }
        }
      },
      immediate: true,
    },
  },

  mounted() {
    this.localItem = { ...this.item }
    this.vehicleStatus = { ...this.item }
    this.sensor = this.vehicleStatus.vehicle?.sensor?.map((s) => s.sensorType.sensorCode) ?? []
  },

  methods: {
    async fetchAddress(data) {
      const address = await this.getAlertAddress(data)
      this.$set(data, 'address', address)
      this.$set(this.vehicleStatus, 'address', address)
    },
    callDriver(isVoip = false) {
      this.track('Click Call Driver on Control Tower V2')
      if (isVoip) {
        const deviceTypeVoip = this.callState.deviceType
        this.track('Control Tower - VoIP Admin make a call', {
          entryPoint: 'Control Tower',
        })
        this.$store.dispatch('voip/startNewCall', {
          dataNewCall: {
            vehicleId: this.callState.vehicleIdVoip,
            licensePlate: this.item?.licensePlate,
            driver: this.item.vehicle.driver1?.fullname ?? null,
            imei: this.callState.imeiVoip,
            deviceType: deviceTypeVoip,
            entryPoint: 'controlTower',
          },
        })
      } else {
        this.$bvModal.show('modal-call')
      }
    },
    endCall() {
      this.track('Control Tower - VoIP End Call', {
        entryPoint: 'Control Tower',
      })
      this.$store.dispatch('voip/endCall', { vehicleId: this.callState.vehicleIdVoip })
    },
    expandMap() {
      this.detailed = !this.detailed
      this.track('Expand Map on Control Tower V2')
    },
    async createCustomInfoWindow(event) {
      let address = {}
      const lat = event.latLng.lat()
      const lng = event.latLng.lng()
      this.selectedPosition = { lat, lng }

      if (lat && lng) {
        const newAddress = await GetAddress(lat, lng)
        address = newAddress.data

        const map = this.$refs.detailAlertMap.$mapObject

        const infowindow = new window.google.maps.InfoWindow({
          content: `<div class="custom-info-window">
            <span class="custom-info-window__title">${address.address.city}</span>
            <p>${address.display_name}</p>
            <a
              href="https://www.google.com/maps?q=${lat},${lng}"
              target="_blank"
              class="url"
              >${this.$t('controlTower.detail.viewOnGMap')}</a
            ></div>`,
          position: event.latLng,
          pixelOffset: new window.google.maps.Size(0, 0),
          options: {
            minWidth: 250,
            maxWidth: 300,
            padding: 0,
          },
        })

        window.google.maps.event.addListenerOnce(infowindow, 'domready', () => {
          const iwOuter = document.querySelector('.gm-style-iw-c')
          iwOuter.style.padding = '0'

          const iwInner = document.querySelector('.gm-style-iw-d')
          iwInner.style.padding = '0'
          iwInner.style.overflow = 'hidden'
        })
        // Close any existing info windows first
        if (this.currentInfoWindow) {
          this.currentInfoWindow.close()
        }

        this.currentInfoWindow = infowindow

        infowindow.open(map)
      }
    },

    async getPlacards() {
      this.loading = true
      const { data } = await this.$apollo.query({
        query: CTOWER_USER_PLACARDS,
        fetchPolicy: 'no-cache',
        variables: {
          filter: {
            vehicleId: this.vehicleStatus.vehicle.id,
          },
        },
      })

      if (data.ctowerUserPlacards.isSuccessful) {
        if (!data.ctowerUserPlacards.ctowerUserPlacards.length) {
          this.loading = false
          return
        }
        this.oldData = data.ctowerUserPlacards.ctowerUserPlacards[0].asArray
        this.layouts = data.ctowerUserPlacards.ctowerUserPlacards[0].asArray.filter(
          (x) => x !== null
        )

        this.loading = false
        this.needUpdate = false
      }
    },
    disabledCheck(param) {
      return !this.isActive || (this.layouts.length === 4 && !this.layouts.includes(param))
    },
    cancel() {
      this.isActive = false
      this.layouts = [...this.oldData]
    },
    cancelModal() {
      this.$bvModal.hide('modal-detail')
    },
    resetModal() {
      this.isActive = false
      this.layouts = []
      this.oldData = []
      this.needUpdate = true
    },
    redirectToLiveview() {
      window.location.href = '/liveview-2/?type=vehicle&vehicleId=' + this.vehicleStatus?.vehicleId
      this.track('Click Lacak Kendaraan from Control Tower V2')
    },
    redirectToTripHistory() {
      window.location.href =
        '/liveview-2/?type=history&vehicleId=' +
        this.vehicleStatus?.vehicleId +
        '&navSource=vehicle'
      this.track('Click Riwayat Perjalanan from Control Tower V2')
    },
    onSubmit() {
      if (!this.layouts.length) {
        return this.$mcToast.warning(this.$t('global.text.noSelected'))
      }

      this.loading = true

      this.$apollo
        .mutate({
          mutation: UPDATE_CTOWER_USER_PLACARD,
          variables: {
            input: {
              oldData: {
                column: this.oldData,
              },
              newData: {
                column: this.layouts,
              },
              vehicleId: this.vehicleStatus.vehicleId,
            },
          },
        })
        .then(({ data }) => {
          if (data.updateCtowerUserPlacard.isSuccessful) {
            this.$emit('onSubmit', this.vehicleStatus.vehicleId, this.layouts)
            this.isActive = false
            this.loading = false
            this.cancelModal()
          }
        })
    },
    shareLocation() {
      this.$bvModal.show('modal-share')
      this.track('Click Bagikan Lokasi from Control Tower V2')
    },
    closePopup(vehicleId) {
      this.$store.dispatch('controlTower/closePopup', Number(vehicleId))
    },

    newWindowTrackVision() {
      let vehicleIdCam = this.vehicleStatus.vehicleId

      if (this.vehicleStatus.children.length > 0) {
        vehicleIdCam = this.vehicleStatus.children[0].vehicleId
      }

      window.open(
        this.$router.resolve({
          name: this.hasAccessAll(['b3h-new-trackvision-menu:read'])
            ? 'Live Streaming Trackvision'
            : 'Track Video V2',
        }).href +
          '?vehicleId=' +
          vehicleIdCam,
        '_blank'
      )
      this.track('Click Pelacakan Video from Control Tower V2')
    },

    handleCollapseExpand(handler) {
      this.$set(this.expanded, handler, !this.expanded[handler])
    },

    async getAlertAddress(alert) {
      let address = '-'
      if (alert.latitude && alert.longitude) {
        const useCache = this.isUsingCache()
        const lat = alert.latitude
        const lon = alert.longitude

        if (useCache) {
          const newAddress = await getGeolocation({ lat, lon })
          address = getAddress(newAddress, lat, lon)?.name || '-'
        } else {
          const newAddress = await GetAddressData(lat, lon)
          address = newAddress.display_name || '-'
        }
      }
      return address
    },

    recenterMap() {
      const map = this.$refs.detailAlertMap.$mapObject
      const center = {
        lat: parseFloat(this.vehicleStatus.latitude),
        lng: parseFloat(this.vehicleStatus.longitude),
      }
      map.setCenter(center)
    },

    cutOffEngine() {
      this.$bvModal.show(`modal-cutoff-engine-${this.vehicleStatus.vehicle?.imei}`)
      this.track('Click Matikan Mesin from Control Tower V2')
    },

    cutOffVehicle() {
      this.$bvModal.show(`modal-cutoff-vehicle-${this.vehicleStatus.vehicle?.imei}`)
      this.track('Click Cut Off/On Vehicle from Control Tower V2')
    },

    async getAlerts(isUrgent = false, isImportant = false, isResolved = false) {
      const res = await this.$apollo.query({
        query: GET_ALERT_CENTER,
        fetchPolicy: 'no-cache',
        variables: {
          filter: {
            startDate: moment(this.period[0]).set('second', 0).toISOString(),
            endDate: moment(this.period[1]).set('second', 59).toISOString(),
            vehicleId: this.vehicleStatus.vehicleId,
            isImportant: isImportant ? isImportant : undefined,
            isUrgent: isUrgent ? isUrgent : undefined,
            isResolved: isResolved,
            pagination: {
              page: 1,
              show: 5,
            },
            orderBy: [JSON.stringify({ createdOn: 'desc' })],
          },
        },
      })

      if (!res.data.rawAlerts.isSuccessful) return
      return res.data.rawAlerts.rawAlerts.data
    },
  },
}
</script>

<style lang="scss" scoped>
:deep(.gm-style-iw-d) {
  max-height: 200px !important;
  overflow-y: auto !important;
}

:deep(.gmnoprint > div > img) {
  cursor: pointer !important;
}

:deep(.gm-style-iw) {
  padding: 16px !important;
}

:deep(.custom-info-window) {
  padding: 0px !important;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  min-width: 250px;
}

:deep(.custom-info-window__title) {
  font-size: 13px;
  font-weight: 400;
  margin-bottom: 8px;
  display: block;
  text-wrap: wrap !important;
}

:deep(.custom-info-window a) {
  color: $info !important;
  font-size: 11px !important;
  margin-top: 8px;
}

:deep(.custom-info-window p) {
  max-width: 200px;
  margin: 4px 0 !important;
  padding: 0 !important;
  font-size: 11px;
  line-height: 1.4;
}

/* Target the InfoWindow wrapper */
.gm-style-iw.gm-style-iw-c {
  padding: 0 !important;
}

/* Target the inner container */
.gm-style-iw-d {
  overflow: hidden !important;
  padding: 0 !important;
}

/* Target the chrome/button container */
:deep(.gm-style-iw-chr) {
  display: none !important;
}

/* Target the close button */
.gm-ui-hover-effect {
  top: 0 !important;
  right: 0 !important;
}

.icon-outline_phone-off {
  &::before {
    color: $red !important;
  }
}
</style>
